/**
 * @file downloadUtils.ts
 * @module downloadUtils
 * @description Utility functions for downloading files and creating zip archives
 */

import JSZip from 'jszip';
import { DownloadConstants } from 'src/constants';

/**
 * Fetches a URL and returns it as a Blob
 * @param {string} url - URL to fetch
 * @param {number} timeoutMs - Request timeout in milliseconds (default: 30000)
 * @returns {Promise<Blob>} The fetched content as a Blob
 * @throws {Error} If the fetch operation fails
 */
const fetchAsBlob = async (
  url: string,
  timeoutMs = DownloadConstants.TIMEOUT_MS,
): Promise<Blob> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
    }
    return response.blob();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Network error while fetching ${url}: ${error.message}`);
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms while fetching ${url}`);
    }
    throw error;
  }
};

/**
 * Downloads a Blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for the download
 */
const downloadBlob = (blob: Blob, filename: string): void => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;

  // Append to DOM before clicking for browser compatibility
  document.body.appendChild(link);
  link.click();
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

/**
 * Downloads a file from a URL
 * @param {string} url - URL to download from
 * @param {string} filename - Filename for the download
 * @throws {Error} If the download operation fails
 */
export const downloadFromUrl = async (url: string, filename: string): Promise<void> => {
  const blob = await fetchAsBlob(url);
  downloadBlob(blob, filename);
};

/**
 * Creates a zip file from multiple URLs
 * @param {Record<string, string>} urlMap - Map of filename to URL
 * @returns {Promise<Blob>} The zip file as a Blob
 * @throws {Error} If zip creation fails
 */
const createZipFromUrls = async (urlMap: Record<string, string>): Promise<Blob> => {
  const zip = new JSZip();

  // Create an array of promises for parallel fetching
  const fetchPromises = Object.entries(urlMap).map(async ([filename, url]) => {
    const blob = await fetchAsBlob(url);
    return { filename, blob };
  });

  // Wait for all fetches to complete in parallel
  const results = await Promise.all(fetchPromises);

  // Add all blobs to the zip file
  results.forEach(({ filename, blob }) => {
    zip.file(filename, blob);
  });

  return zip.generateAsync({ type: 'blob' });
};

/**
 * Downloads multiple files as a zip archive
 * @param {Record<string, string>} urlMap - Map of filename to URL
 * @param {string} zipFilename - Name for the zip file
 * @throws {Error} If the zip download operation fails
 */
export const downloadZipFromUrls = async (
  urlMap: Record<string, string>,
  zipFilename: string,
): Promise<void> => {
  const zipBlob = await createZipFromUrls(urlMap);
  downloadBlob(zipBlob, zipFilename);
};
